import { messengerHttp, messengerError } from "@/helpers/messengerHttp";

// proship-messenger API. Callback style like orders.js: payload.callback(true, data) or
// payload.callback(false, {status, code, detail}). State stays empty: vuex-persist writes every
// module to localStorage, and Pages or send history must not outlive the seller's session.

const SEND_BATCH_LIMIT = 2500; // POST /api/tracking-sends/batch accepts at most this many ids

// One GET /api/pages per app session decides whether the seller sees Messenger in the orders table.
// Module-level and never persisted; any error counts as "no Pages".
let hasPagesRequest = null;

// GET ship-settings, cached per Page for the app session (the Page rows and the shop-choice
// screen read it; a shop save or a force flag re-reads it).
const shipSettingsCache = {};

export const clearShipSettingsCache = (pageId) => {
  if (pageId) delete shipSettingsCache[String(pageId)];
  else Object.keys(shipSettingsCache).forEach((key) => delete shipSettingsCache[key]);
};

// Claim response shops for needs_choice Pages, keyed by pageId. The connect flow stashes them
// here so the shop-choice route needs only the pageId in the URL; each entry is taken once.
const claimShops = {};

export const rememberClaimShops = (pageId, info) => {
  claimShops[String(pageId)] = info;
};

export const takeClaimShops = (pageId) => {
  const key = String(pageId);
  const info = claimShops[key] || null;
  delete claimShops[key];
  return info;
};

const done = (payload, ok, data) => {
  if (payload && payload.callback) payload.callback(ok, data);
};

// .then(onOk, onFail) rather than .catch so an error thrown inside a success callback is not
// reported to the same callback a second time as an API failure.
const settle = (request, payload, pick) =>
  request.then(
    (response) => {
      const data = (response && response.data) || {};
      if (data.success === false) return done(payload, false, messengerError({ response }));
      done(payload, true, pick ? pick(data) : data);
    },
    (err) => done(payload, false, messengerError(err))
  );

// Epoch ms from epoch ms, a numeric string or an ISO string; null when absent or unreadable.
export const toMs = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value;
  if (/^\d+$/.test(String(value))) return Number(value);
  const parsed = Date.parse(value);
  return isNaN(parsed) ? null : parsed;
};

// GET /api/pages is an existing snake_case route; this is the one place it becomes camelCase.
// camelCase siblings are tolerated (the r2 payload documents pageId-style keys).
const pickField = (p, snake, camel) => (p[snake] != null ? p[snake] : p[camel]);

const mapPage = (p) => {
  const rawId = pickField(p, "page_id", "pageId") != null ? pickField(p, "page_id", "pageId") : p.id;
  return {
    pageId: String(rawId),
    name: p.name || String(rawId),
    category: p.category || null,
    pictureUrl: p.profile_pic || p.pictureUrl || p.picture || null,
    link: p.link || null,
    status: p.status,
    needsReauth: p.needs_reauth === true || p.needsReauth === true,
    utilityMessaging: typeof p.utility_messaging === "boolean" ? p.utility_messaging : null,
    error: p.error || null,
    shop: p.shop != null ? p.shop : null, // {id, name} or a bare shop id, when the payload carries it
    shipEnabled: pickField(p, "ship_enabled", "shipEnabled") != null ? pickField(p, "ship_enabled", "shipEnabled") !== false : null,
    ordersCount: Number(pickField(p, "orders_count", "ordersCount")) || 0,
    connectedAt: toMs(pickField(p, "connected_at", "connectedAt")),
    updatedAt: toMs(pickField(p, "updated_at", "updatedAt")),
  };
};

// A Page whose features work: connected (or carried over as imported) and not flagged for re-auth
export const isPageConnected = (page) =>
  !!page && (page.status === "connected" || page.status === "imported") && page.needsReauth !== true;

const pagePath = (pageId) => "/api/pages/" + encodeURIComponent(pageId);

export default {
  namespaced: true,
  state: {},

  actions: {
    fetchPages(context, payload) {
      settle(messengerHttp.get("/api/pages"), payload, (data) =>
        (Array.isArray(data.data) ? data.data : []).map(mapPage)
      );
    },

    // Resolves true when the seller has at least one Page. Memoised for the app session.
    hasPages() {
      if (!hasPagesRequest) {
        hasPagesRequest = messengerHttp.get("/api/pages").then(
          (response) => {
            const data = (response && response.data) || {};
            return data.success !== false && Array.isArray(data.data) && data.data.length > 0;
          },
          () => false
        );
      }
      return hasPagesRequest;
    },

    connectUrl(context, payload) {
      settle(
        messengerHttp.post("/api/pages/connect-url", { redirectUrl: payload.redirectUrl }),
        payload,
        (data) => data.url
      );
    },

    // Back from Facebook with ?claim=<nonce>: binds the Pages connected in that flow to this seller.
    // Until it succeeds they are not in GET /api/pages. Resolves with the raw response; its
    // pages[] entries each carry shopLink: linked | already | needs_choice | failed.
    claimPages(context, payload) {
      settle(messengerHttp.post("/api/pages/claim", { claim: payload.claim }), payload);
    },

    // Which shop a Page sells for. The ship-settings cache for the Page is dropped: the next
    // read sees the new shop.
    setPageShop(context, payload) {
      const pageId = String(payload.pageId);
      const request = messengerHttp.post(pagePath(payload.pageId) + "/shop", { shopId: payload.shopId }).then(
        (response) => {
          delete shipSettingsCache[pageId];
          return response;
        }
      );
      settle(request, payload);
    },

    // {ship_enabled, ship_shop_id, shops:[{shopId, name}]}; cached per Page unless payload.force.
    fetchShipSettings(context, payload) {
      const pageId = String(payload.pageId);
      if (!payload.force && shipSettingsCache[pageId]) return done(payload, true, shipSettingsCache[pageId]);
      settle(messengerHttp.get(pagePath(payload.pageId) + "/ship-settings"), payload, (data) => {
        shipSettingsCache[pageId] = data;
        return data;
      });
    },

    // เปิดบิลในแชท on/off for one Page
    updateShipSettings(context, payload) {
      const pageId = String(payload.pageId);
      const request = messengerHttp
        .put(pagePath(payload.pageId) + "/ship-settings", { ship_enabled: payload.shipEnabled })
        .then((response) => {
          if (shipSettingsCache[pageId]) {
            shipSettingsCache[pageId] = Object.assign({}, shipSettingsCache[pageId], {
              ship_enabled: payload.shipEnabled,
            });
          }
          return response;
        });
      settle(request, payload);
    },

    disconnectPage(context, payload) {
      settle(messengerHttp.delete(pagePath(payload.pageId)), payload);
    },

    fetchTemplate(context, payload) {
      settle(messengerHttp.get(pagePath(payload.pageId) + "/tracking-template"), payload);
    },

    // payload.body: the wording with {{tracking}}/{{cod}}/{{name}}, or null to go back to the default
    saveTemplate(context, payload) {
      settle(
        messengerHttp.put(pagePath(payload.pageId) + "/tracking-template", { body: payload.body }),
        payload
      );
    },

    sendTestMessage(context, payload) {
      const body = { phone: payload.phone };
      if (payload.codAmount != null && String(payload.codAmount).trim() !== "") {
        body.codAmount = String(payload.codAmount).trim();
      }
      settle(messengerHttp.post(pagePath(payload.pageId) + "/tracking-template/test", body), payload);
    },

    // One dispatch per page of orders; split into requests of at most SEND_BATCH_LIMIT ids.
    // Resolves with {[orderId]: latest send}; an order with nothing on file has no key.
    fetchSendsBatch(context, payload) {
      const ids = Array.from(new Set((payload.orderIds || []).filter(Boolean).map(String)));
      if (!ids.length) return done(payload, true, {});

      const chunks = [];
      for (let i = 0; i < ids.length; i += SEND_BATCH_LIMIT) chunks.push(ids.slice(i, i + SEND_BATCH_LIMIT));

      Promise.all(chunks.map((orderIds) => messengerHttp.post("/api/tracking-sends/batch", { orderIds }))).then(
        (responses) => {
          const byOrderId = {};
          responses.forEach((response) => Object.assign(byOrderId, (response.data && response.data.byOrderId) || {}));
          done(payload, true, byOrderId);
        },
        (err) => done(payload, false, messengerError(err))
      );
    },

    fetchOrderMessages(context, payload) {
      settle(
        messengerHttp.get("/api/orders/" + encodeURIComponent(payload.orderId) + "/tracking-messages"),
        payload,
        (data) => (Array.isArray(data.messages) ? data.messages : [])
      );
    },
  },
};
